import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  ListSubheader,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import { useTenant } from "../hooks/useTenant";
import useRequests from "../hooks/http";

const PAGE_SIZE = 100;
const LIVE_INTERVAL_MS = 5000;

const OUTCOME_COLORS: Record<string, "success" | "error" | "default"> = {
  success: "success",
  failure: "error",
};

const USER_ACTIONS = new Set([
  "user.invited",
  "user.role.added",
  "user.permission.added",
  "user.access.granted",
  "user.access.revoked",
  "user.deactivated",
]);

const APPLICATION_ACTIONS = new Set([
  "application.created",
  "application.updated",
  "application.client.created",
  "application.client.updated",
  "auth.token.issued",
  "auth.login",
  "auth.login.failed",
]);

const ROLE_ACTIONS = new Set([
  "role.created",
  "role.updated",
  "role.deleted",
  "role.permission.added",
  "role.permission.removed",
]);

function targetPath(entry: LogEntry): string | null {
  const { action, entity_id } = entry;
  if (action === "permission.created" && entity_id) {
    return `/app/applications/${entity_id}/permissions`;
  }
  if (USER_ACTIONS.has(action)) return "/app/users";
  if (APPLICATION_ACTIONS.has(action)) return "/app/applications";
  if (ROLE_ACTIONS.has(action)) return "/app/roles";
  return null;
}

interface LogEntry {
  ts: string;
  action: string;
  actor?: string;
  tenant?: string;
  target?: string;
  entity_id?: string;
  outcome?: string;
  detail?: string;
}

interface ActionGroup {
  label: string;
  actions: { value: string; label: string }[];
}

export default function Logs() {
  const { t } = useTranslation();
  const { get } = useRequests();
  const { activeTenant, loading: tenantLoading } = useTenant();

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [filterAction, setFilterAction] = useState("");
  const [filterActor, setFilterActor] = useState("");

  const [actionGroups, setActionGroups] = useState<ActionGroup[]>([]);
  const [liveMode, setLiveMode] = useState(false);
  const newestTsRef = useRef<string | null>(null);
  const liveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!activeTenant) return;
    get(`/admin/tenants/${activeTenant.id}/audit-log-actions`).then(
      ({ data, status }) => {
        if (status === 200 && Array.isArray(data)) setActionGroups(data);
      },
    );
  }, [activeTenant, get]);

  const fetchPage = useCallback(
    (pageNum: number) => {
      if (!activeTenant) return;
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (filterAction) params.set("action", filterAction);
      if (filterActor) params.set("actor", filterActor);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String((pageNum - 1) * PAGE_SIZE));
      get(
        `/admin/tenants/${activeTenant.id}/audit-logs?${params.toString()}`,
      ).then(({ data, status }) => {
        if (status === 200 && Array.isArray(data)) {
          setEntries(data);
          setHasMore(data.length === PAGE_SIZE);
          if (data.length > 0) newestTsRef.current = data[0].ts;
        } else {
          setError("Failed to load audit logs");
        }
        setLoading(false);
      });
    },
    [activeTenant, filterAction, filterActor, get],
  );

  useEffect(() => {
    setPage(1);
    fetchPage(1);
  }, [activeTenant, filterAction, filterActor, fetchPage]);

  const pollForNew = useCallback(() => {
    if (!activeTenant || !newestTsRef.current) return;
    const params = new URLSearchParams();
    if (filterAction) params.set("action", filterAction);
    if (filterActor) params.set("actor", filterActor);
    params.set("since", newestTsRef.current);
    get(
      `/admin/tenants/${activeTenant.id}/audit-logs?${params.toString()}`,
    ).then(({ data, status }) => {
      if (status === 200 && Array.isArray(data) && data.length > 0) {
        newestTsRef.current = data[0].ts;
        setEntries((prev) => [...data, ...prev]);
      }
    });
  }, [activeTenant, filterAction, filterActor, get]);

  useEffect(() => {
    if (liveMode) {
      liveTimerRef.current = setInterval(pollForNew, LIVE_INTERVAL_MS);
    } else {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    }
    return () => {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    };
  }, [liveMode, pollForNew]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchPage(value);
  };

  if (tenantLoading) return <LinearProgress />;

  return (
    <Box>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4">{t("logs.title", "Logs")}</Typography>
        <Button
          size="small"
          variant={liveMode ? "contained" : "outlined"}
          color={liveMode ? "success" : "inherit"}
          startIcon={liveMode ? <PauseIcon /> : <PlayArrowIcon />}
          onClick={() => setLiveMode((v) => !v)}
        >
          {liveMode ? "Live — pause" : "Go live"}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          "logs.description",
          "Audit trail of key actions taken within this workspace. Includes authentication events, user management, and access changes.",
        )}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Action</InputLabel>
          <Select
            value={filterAction}
            label="Action"
            onChange={(e) => {
              setFilterAction(e.target.value);
              setLiveMode(false);
            }}
          >
            <MenuItem value="">All actions</MenuItem>
            {actionGroups.map((group) => [
              <ListSubheader key={group.label}>{group.label}</ListSubheader>,
              ...group.actions.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Actor"
          placeholder="Filter by email"
          value={filterActor}
          onChange={(e) => {
            setFilterActor(e.target.value);
            setLiveMode(false);
          }}
          sx={{ minWidth: 220 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && entries.length === 0 ? (
        <Typography color="text.secondary">No log entries found.</Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow key={`${e.ts}-${i}`}>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        fontFamily: "monospace",
                        fontSize: 12,
                      }}
                    >
                      {new Date(e.ts).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize={12}
                      >
                        {e.action}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize={13}>
                        {e.actor ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const path = targetPath(e);
                        if (e.target && path) {
                          return (
                            <Link
                              component={RouterLink}
                              to={path}
                              variant="body2"
                              fontSize={13}
                              underline="hover"
                            >
                              {e.target}
                            </Link>
                          );
                        }
                        return (
                          <Typography variant="body2" fontSize={13}>
                            {e.target ?? "—"}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={e.outcome ?? "success"}
                        color={
                          OUTCOME_COLORS[e.outcome ?? "success"] ?? "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize={12}
                      >
                        {e.detail ?? ""}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!liveMode && (page > 1 || hasMore) && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                page={page}
                count={hasMore ? page + 1 : page}
                onChange={handlePageChange}
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
